import { atom } from 'jotai';
import type { BulkLotRenumberValidationErrorResponse } from '@/types/lot/bulk-renumber.types';

/**
 * Atom for controlling the visibility of the bulk lot renumber upload modal (WBPR-4487)
 */
export const showBulkLotRenumberModalAtom = atom<boolean>(false);

/**
 * Atom for parking the accepted renumber job id, keyed by auction GUID.
 *
 * Written by `usePostBulkLotRenumber` on a 202 so the catalog progress callout can pick the job up
 * without a discovery round-trip, and by `useBulkLotRenumberFeedback` when it adopts a non-terminal
 * job discovered via the latest-list query (D-N10). Cleared by `useBulkLotRenumberFeedback` once the
 * tracked job's completion has been observed and toasted (D-N7).
 */
export const activeLotRenumberJobAtom = atom<Record<string, { jobId: string }>>({});

/**
 * Stashes the normalized row-level 422 body per auction (D-N3) so the catalog error callout and the
 * dedicated error page can both read it without re-parsing the response. Written by
 * `usePostBulkLotRenumber` on a row-level 422; cleared on the next accepted (202) submit and on
 * adoption of a newer non-terminal job discovered elsewhere (a fresher attempt always supersedes a
 * stale "no changes made" callout — keeps the red and blue callouts mutually exclusive).
 *
 * Deliberately an in-memory atom, not `atomWithStorage`: a full page refresh clears this state by
 * design — the durable artifact is WBPR-4863's downloadable error report, not this atom.
 */
export const bulkLotRenumberValidationErrorAtom = atom<Record<string, BulkLotRenumberValidationErrorResponse>>({});

/**
 * Tracks which NEEDS_ATTENTION job id the seller dismissed the sync-issue callout for, per auction
 * (D-N8). In-memory only, matching `bulkLotRenumberValidationErrorAtom` — the callout reappears on
 * the next visit as long as the job is still stuck, which is the desired "dismiss for the session"
 * behavior rather than a permanent dismissal.
 */
export const dismissedBulkLotRenumberSyncIssueAtom = atom<Record<string, string>>({});
